import { useSyncExternalStore } from "react";
import { prepareUploadFile } from "../../../../helpers/prepareUploadFile.js";
import { fetchWithCustomerRequestAuth } from "../../../../helpers/customerRequestAuth.js";
import { requestImageUploadPermit } from "../../fotos/fotos-schiessen/layout/uploadConcurrencyGate.js";
import {
  deleteServiceHistoryUploadRecord,
  getServiceHistoryUploadRecordsByOwner,
  putServiceHistoryUploadRecord,
  updateServiceHistoryUploadRecord,
} from "./serviceHistoryUploadStore.js";

const MIN_UPLOAD_TIMEOUT_MS = 5 * 60_000;
const MAX_UPLOAD_TIMEOUT_MS = 15 * 60_000;
const MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND = 4 * 1024;
const UPLOAD_TIMEOUT_OVERHEAD_MS = 60_000;
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000, 60_000];
const VISIBLE_STATES = new Set(["pending", "committed", "failed"]);
const EMPTY_SNAPSHOT = Object.freeze({});
const EMPTY_STATUS = Object.freeze({
  hydrated: false,
  ownerEmail: null,
  persistenceAvailable: true,
  storageDefinitelyUnavailable: false,
});

const records = new Map();
const jobs = new Map();
const retryTimers = new Map();
const discardRequests = new Set();
const previewUrls = new Map();
const listeners = new Set();
const queue = [];

let snapshot = EMPTY_SNAPSHOT;
let registryStatus = EMPTY_STATUS;
let apiBaseUrl = "";
let currentOwnerEmail = null;
let hydrationPromise = null;
let hydrationGeneration = 0;
let activeJob = null;
let onlineListenerInstalled = false;
let uploadTimeoutOverrideMs = null;
let loadRecordsByOwner = getServiceHistoryUploadRecordsByOwner;
let persistRecord = putServiceHistoryUploadRecord;

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function createUploadId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

export function makeServiceHistoryUploadKey(vehicleId, uploadId) {
  return `${String(vehicleId || "")}::service-history::${String(
    uploadId || "",
  )}`;
}

function emit() {
  listeners.forEach((listener) => listener());
}

function publishStatus(patch) {
  registryStatus = Object.freeze({ ...registryStatus, ...patch });
  emit();
}

function publishEntry(key, entry) {
  snapshot = Object.freeze({ ...snapshot, [key]: Object.freeze(entry) });
  emit();
}

function removeEntry(key) {
  if (!snapshot[key]) return;
  const next = { ...snapshot };
  delete next[key];
  snapshot = Object.freeze(next);
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getServiceHistoryUploadSnapshot() {
  return snapshot;
}

export function getServiceHistoryUploadRegistryStatus() {
  return registryStatus;
}

export function useServiceHistoryUploadSnapshot() {
  return useSyncExternalStore(
    subscribe,
    getServiceHistoryUploadSnapshot,
    () => EMPTY_SNAPSHOT,
  );
}

export function useServiceHistoryUploadRegistryStatus() {
  return useSyncExternalStore(
    subscribe,
    getServiceHistoryUploadRegistryStatus,
    () => EMPTY_STATUS,
  );
}

function createDeferred() {
  let resolve;
  const promise = new Promise((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve, settled: false };
}

function settle(job, result) {
  if (job.completion.settled) return;
  job.completion.settled = true;
  job.completion.resolve(result);
}

function isPdfRecord(record) {
  return (
    String(record?.contentType || "").toLowerCase() === "application/pdf" ||
    /\.pdf$/i.test(String(record?.fileName || ""))
  );
}

function createPreviewUrl(record) {
  if (
    !record?.blob ||
    isPdfRecord(record) ||
    !globalThis.URL?.createObjectURL
  ) {
    return null;
  }
  const existing = previewUrls.get(record.key);
  if (existing) globalThis.URL.revokeObjectURL?.(existing);
  const next = globalThis.URL.createObjectURL(record.blob);
  previewUrls.set(record.key, next);
  return next;
}

function releasePreviewUrl(key) {
  const url = previewUrls.get(key);
  if (url) globalThis.URL?.revokeObjectURL?.(url);
  previewUrls.delete(key);
}

function entryFromRecord(record, previewUrl = null) {
  return {
    key: record.key,
    ownerEmail: record.ownerEmail,
    vehicleId: record.vehicleId,
    uploadId: record.uploadId,
    generation: Number(record.generation || 1),
    capturedAt: record.capturedAt,
    state: record.state,
    phase: record.phase || null,
    attempts: Number(record.attempts || 0),
    fileName: record.fileName || "Serviceheft-Datei",
    step: record.fileName || "Serviceheft-Datei",
    contentType: record.contentType || "",
    previewUrl: previewUrl || record.previewUrl || null,
    imageUrl: record.imageUrl || null,
    hasLocalBlob: Boolean(record.blob),
    persisted: record.persisted !== false,
    retryable: record.retryable !== false,
    errorName: record.errorName || null,
    serverRequestStarted: record.serverRequestStarted === true,
    errorCode: record.errorCode || null,
    errorMessage: record.errorMessage || null,
    httpStatus: Number(record.httpStatus || 0) || null,
  };
}

function isJobCurrent(job) {
  return Boolean(
    job &&
      jobs.get(job.key) === job &&
      records.get(job.key)?.uploadId === job.record.uploadId &&
      !["confirmed", "deleted"].includes(records.get(job.key)?.state),
  );
}

function clearRetryTimer(key) {
  const timer = retryTimers.get(key);
  if (timer) clearTimeout(timer);
  retryTimers.delete(key);
}

function abortJob(job, result = { ok: false, cancelled: true }) {
  if (!job) return;
  job.controller?.abort();
  job.cancelRequest?.();
  job.uploadPermit?.cancel();
  job.uploadPermit = null;
  clearTimeout(job.timeoutId);
  job.timeoutId = null;
  clearRetryTimer(job.key);
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (queue[index] === job) queue.splice(index, 1);
  }
  if (activeJob === job) activeJob = null;
  settle(job, result);
  scheduleQueue();
}

function reconcileAfterPersistenceCasLoss(job) {
  if (!job) return;
  settle(job, { ok: false, externalReplacement: true });
  if (jobs.get(job.key) === job) jobs.delete(job.key);
  if (records.get(job.key)?.uploadId === job.record.uploadId) {
    records.delete(job.key);
    releasePreviewUrl(job.key);
    removeEntry(job.key);
  }
}

async function parseResponse(response) {
  const contentType = response.headers?.get?.("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => "");
  return text ? { error: text } : {};
}

function retryAfterMs(response) {
  const value = response.headers?.get?.("retry-after");
  if (!value) return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
}

function createUploadError(response, result) {
  const error = new Error(
    result?.error ||
      result?.message ||
      "Serviceheft-Datei konnte nicht hochgeladen werden.",
  );
  error.name = "ServiceHistoryUploadError";
  error.status = response.status;
  error.code = result?.code || null;
  error.retryAfterMs = retryAfterMs(response);
  error.retryable =
    response.status === 401 ||
    response.status === 408 ||
    response.status === 425 ||
    response.status === 429 ||
    response.status >= 500;
  return error;
}

async function prepareRecordFile(record) {
  const sourceFile = new File(
    [record.blob],
    record.fileName || "service-history-file",
    {
      type: record.contentType || record.blob?.type || "application/octet-stream",
      lastModified: Number(record.lastModified || Date.now()),
    },
  );
  try {
    const prepared = await prepareUploadFile(sourceFile, {
      maxLongEdge: 1920,
      targetMaxBytes: 1100 * 1024,
    });
    return prepared.file || sourceFile;
  } catch (error) {
    console.error("Serviceheft-Datei konnte nicht vorbereitet werden:", error);
    return sourceFile;
  }
}

async function ensurePreparedRecord(record) {
  if (record.prepared === true) return true;
  const preparedFile = await prepareRecordFile(record);
  return persistPatch(record, {
    blob: preparedFile,
    fileName: preparedFile.name || record.fileName,
    contentType:
      preparedFile.type || record.contentType || "application/octet-stream",
    lastModified: Number(preparedFile.lastModified || Date.now()),
    prepared: true,
    phase: "queued",
  });
}

function getValidatedReservation(record, result) {
  const generation = Number(result?.generation);
  if (
    result?.uploadId !== record.uploadId ||
    generation !== Number(record.generation || 1)
  ) {
    const error = new Error(
      "Der Server hat den Serviceheft-Upload nicht eindeutig reserviert.",
    );
    error.name = "ServiceHistoryUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const status = String(result?.status || "");
  if (!["pending", "main_uploaded"].includes(status)) {
    const error = new Error(
      "Der Server hat den Serviceheft-Upload nicht reserviert.",
    );
    error.name = "ServiceHistoryUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const files = Array.isArray(result?.files) ? result.files : [];
  const imageUrl =
    files.length === 1 && typeof files[0] === "string"
      ? files[0].trim()
      : "";
  if (status === "main_uploaded") {
    try {
      if (!["http:", "https:"].includes(new URL(imageUrl).protocol)) {
        throw new Error("invalid protocol");
      }
    } catch {
      const error = new Error(
        "Der Server meldet einen fertigen Serviceheft-Upload ohne Datei-URL.",
      );
      error.name = "ServiceHistoryUploadProtocolError";
      error.retryable = true;
      throw error;
    }
  }

  return {
    generation,
    completed: status === "main_uploaded",
    imageUrl: imageUrl || null,
  };
}

async function reserveRecord(record, signal) {
  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    "",
  );
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Serviceheft-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/service-history/upload/reserve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: record.ownerEmail,
        id: record.vehicleId,
        uploadId: record.uploadId,
        capturedAt: record.capturedAt,
        uploadGeneration: Number(record.generation || 1),
      }),
      signal,
    },
  );
  const result = await parseResponse(response);
  if (!response.ok) throw createUploadError(response, result);
  return getValidatedReservation(record, result);
}

async function cancelFailedRecord(record) {
  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    "",
  );
  if (!baseUrl) {
    const error = new Error(
      "Backend-Adresse zum Verwerfen des Serviceheft-Uploads fehlt.",
    );
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/service-history/upload/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: record.ownerEmail,
        id: record.vehicleId,
        uploadId: record.uploadId,
        capturedAt: record.capturedAt,
        uploadGeneration: Number(record.generation || 1),
      }),
    },
  );
  const result = await parseResponse(response);
  if (!response.ok) throw createUploadError(response, result);

  const cancellationConfirmed = Boolean(
    result?.uploadId === record.uploadId &&
      Number(result?.capturedAt) === Number(record.capturedAt) &&
      Number(result?.generation) === Number(record.generation || 1) &&
      (result?.cancelled === true ||
        result?.alreadyTerminal === true ||
        result?.noPendingClaim === true),
  );
  if (!cancellationConfirmed) {
    const error = new Error(
      "Der Server hat das Verwerfen des Serviceheft-Uploads nicht eindeutig bestätigt.",
    );
    error.name = "ServiceHistoryUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  return result;
}

async function uploadRecord(record, signal) {
  if (!record.blob) {
    const error = new Error("Die lokal gespeicherte Serviceheft-Datei fehlt.");
    error.retryable = false;
    throw error;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const error = new Error("Keine Internetverbindung.");
    error.name = "OfflineServiceHistoryUploadError";
    error.retryable = true;
    throw error;
  }

  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    "",
  );
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Serviceheft-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const preparedFile = new File(
    [record.blob],
    record.fileName || "service-history-file",
    {
      type:
        record.contentType ||
        record.blob?.type ||
        "application/octet-stream",
      lastModified: Number(record.lastModified || Date.now()),
    },
  );
  const form = new FormData();
  form.append("email", record.ownerEmail);
  form.append("id", record.vehicleId);
  form.append("uploadId", record.uploadId);
  form.append("capturedAt", String(record.capturedAt));
  form.append("uploadGeneration", String(record.generation || 1));
  form.append(
    "files",
    preparedFile,
    preparedFile.name || record.fileName || "service-history-file",
  );

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/service-history/upload`,
    {
      method: "POST",
      body: form,
      signal,
    },
  );
  const result = await parseResponse(response);
  if (!response.ok) throw createUploadError(response, result);

  const files = Array.isArray(result?.files) ? result.files : [];
  const imageUrl =
    files.length === 1 && typeof files[0] === "string"
      ? files[0].trim()
      : "";
  let validUrl = false;
  try {
    validUrl = ["http:", "https:"].includes(new URL(imageUrl).protocol);
  } catch {}
  const generation = Number(result?.generation);
  if (
    !validUrl ||
    result?.uploadId !== record.uploadId ||
    result?.status !== "main_uploaded" ||
    generation !== 1
  ) {
    const error = new Error(
      "Der Server hat den Serviceheft-Upload nicht eindeutig bestätigt.",
    );
    error.name = "ServiceHistoryUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  return { imageUrl, generation };
}

function getUploadTimeoutMs(record) {
  if (uploadTimeoutOverrideMs !== null) return uploadTimeoutOverrideMs;
  const size = Math.max(0, Number(record?.blob?.size || 0));
  const sizeAwareTimeout =
    size > 0
      ? Math.ceil(
          (size / MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND) * 1000,
        ) + UPLOAD_TIMEOUT_OVERHEAD_MS
      : 0;
  return Math.min(
    MAX_UPLOAD_TIMEOUT_MS,
    Math.max(MIN_UPLOAD_TIMEOUT_MS, sizeAwareTimeout),
  );
}

function uploadWithDeadline(job, record) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (handler, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(job.timeoutId);
      job.timeoutId = null;
      job.cancelRequest = null;
      handler(value);
    };

    job.timeoutId = setTimeout(() => {
      job.controller?.abort();
      const error = new Error(
        "Der Serviceheft-Upload hat zu lange gedauert.",
      );
      error.name = "ServiceHistoryUploadTimeoutError";
      error.retryable = true;
      finish(reject, error);
    }, getUploadTimeoutMs(record));
    job.cancelRequest = () => {
      const error = new Error("Der Serviceheft-Upload wurde abgebrochen.");
      error.name = "AbortError";
      error.retryable = false;
      finish(reject, error);
    };

    // WebKit kann ein fetch trotz AbortController offen lassen. Die äußere
    // Deadline gibt den globalen Upload-Slot dennoch frei; ein später Commit
    // konvergiert über dieselbe idempotente uploadId.
    Promise.resolve()
      .then(async () => {
        const reservation = await reserveRecord(
          record,
          job.controller.signal,
        );
        const reserved = await persistPatch(record, {
          generation: reservation.generation,
          phase: reservation.completed ? "committing" : "uploading",
        });
        if (!reserved || !isJobCurrent(job)) {
          const error = new Error(
            "Der Serviceheft-Upload wurde lokal ersetzt.",
          );
          error.name = "ServiceHistoryUploadPersistenceCasError";
          error.retryable = false;
          throw error;
        }
        publishEntry(
          record.key,
          entryFromRecord(record, snapshot[record.key]?.previewUrl || null),
        );
        if (reservation.completed) {
          return {
            imageUrl: reservation.imageUrl,
            generation: reservation.generation,
          };
        }
        return uploadRecord(record, job.controller.signal);
      })
      .then(
        (result) => finish(resolve, result),
        (error) => finish(reject, error),
      );
  });
}

async function persistPatch(record, patch) {
  const updatedAt = Date.now();
  if (record.persisted === false) {
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  }
  try {
    const updated = await updateServiceHistoryUploadRecord(
      record.key,
      record.uploadId,
      { ...patch, updatedAt },
    );
    if (!updated) return false;
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  } catch (error) {
    console.error(
      "Serviceheft-Uploadstatus konnte nicht gespeichert werden:",
      error,
    );
    Object.assign(record, patch, { updatedAt, persisted: false });
    records.set(record.key, record);
    publishStatus({ persistenceAvailable: false });
    return true;
  }
}

function retryDelay(record, error) {
  const index = Math.min(
    Number(record.attempts || 0),
    RETRY_DELAYS_MS.length - 1,
  );
  return Math.max(
    RETRY_DELAYS_MS[index],
    Number(error?.retryAfterMs || 0),
  );
}

async function scheduleRetry(job, error) {
  if (!isJobCurrent(job)) return;
  const record = job.record;
  const delay = retryDelay(record, error);
  const persisted = await persistPatch(record, {
    state: "pending",
    phase: "retry_wait",
    attempts: Number(record.attempts || 0) + 1,
    retryable: true,
    errorName: error?.name || null,
    errorCode: error?.code || null,
    errorMessage: error?.message || "Upload wird automatisch wiederholt.",
    httpStatus: Number(error?.status || 0) || null,
    nextRetryAt: Date.now() + delay,
  });
  if (!persisted || !isJobCurrent(job)) return;
  publishEntry(
    record.key,
    entryFromRecord(record, snapshot[record.key]?.previewUrl || null),
  );
  clearRetryTimer(record.key);
  retryTimers.set(
    record.key,
    setTimeout(() => {
      retryTimers.delete(record.key);
      if (isJobCurrent(job)) enqueueJob(job);
    }, delay),
  );
}

async function runJob(job) {
  if (!isJobCurrent(job) || job.running) return;
  job.running = true;
  activeJob = job;
  job.uploadPermit = requestImageUploadPermit(job);
  const record = job.record;

  try {
    if (!(await job.uploadPermit.promise) || !isJobCurrent(job)) return;
    job.controller = new AbortController();
    const prepared = await ensurePreparedRecord(record);
    if (!prepared) {
      reconcileAfterPersistenceCasLoss(job);
      return;
    }
    if (!isJobCurrent(job)) return;
    const canStart = await persistPatch(record, {
      state: "pending",
      phase: "reserving",
      serverRequestStarted: true,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
    });
    if (!canStart) {
      reconcileAfterPersistenceCasLoss(job);
      return;
    }
    if (!isJobCurrent(job)) return;
    publishEntry(
      record.key,
      entryFromRecord(record, snapshot[record.key]?.previewUrl || null),
    );

    const result = await uploadWithDeadline(job, record);
    if (!isJobCurrent(job)) return;
    const committed = await persistPatch(record, {
      state: "committed",
      phase: null,
      blob: null,
      imageUrl: result.imageUrl,
      generation: result.generation,
      retryable: false,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
      nextRetryAt: null,
    });
    if (!committed) {
      reconcileAfterPersistenceCasLoss(job);
      return;
    }
    if (!isJobCurrent(job)) return;
    releasePreviewUrl(record.key);
    publishEntry(record.key, entryFromRecord(record));
    settle(job, { ok: true, ...result });
    if (jobs.get(job.key) === job) jobs.delete(job.key);
  } catch (error) {
    if (!isJobCurrent(job)) return;
    if (error?.name === "ServiceHistoryUploadPersistenceCasError") {
      reconcileAfterPersistenceCasLoss(job);
    } else if (error?.name === "AbortError") {
      settle(job, { ok: false, cancelled: true });
    } else if (error?.retryable !== false) {
      await scheduleRetry(job, error);
    } else {
      const failed = await persistPatch(record, {
        state: "failed",
        phase: null,
        retryable: false,
        errorName: error?.name || null,
        errorCode: error?.code || null,
        errorMessage:
          error?.message || "Serviceheft-Datei konnte nicht hochgeladen werden.",
        httpStatus: Number(error?.status || 0) || null,
      });
      if (!failed || !isJobCurrent(job)) return;
      publishEntry(
        record.key,
        entryFromRecord(record, snapshot[record.key]?.previewUrl || null),
      );
      settle(job, { ok: false, error });
      if (jobs.get(job.key) === job) jobs.delete(job.key);
    }
  } finally {
    clearTimeout(job.timeoutId);
    job.timeoutId = null;
    job.controller = null;
    job.cancelRequest = null;
    job.uploadPermit?.cancel();
    job.uploadPermit = null;
    job.running = false;
    if (activeJob === job) activeJob = null;
    scheduleQueue();
  }
}

function scheduleQueue() {
  if (activeJob) return;
  while (queue.length) {
    const job = queue.shift();
    if (!isJobCurrent(job) || job.running) continue;
    void runJob(job);
    return;
  }
}

function enqueueJob(job) {
  if (!isJobCurrent(job) || job.running || queue.includes(job)) return;
  queue.push(job);
  scheduleQueue();
}

function createJob(record) {
  const job = {
    key: record.key,
    record,
    completion: createDeferred(),
    controller: null,
    cancelRequest: null,
    timeoutId: null,
    running: false,
    uploadPermit: null,
  };
  jobs.set(record.key, job);
  return job;
}

function adoptStoredRecord(storedRecord) {
  if (
    !storedRecord ||
    normalizeEmail(storedRecord.ownerEmail) !== currentOwnerEmail
  ) {
    return false;
  }
  const existing = records.get(storedRecord.key);
  if (
    existing &&
    Number(existing.updatedAt || 0) > Number(storedRecord.updatedAt || 0)
  ) {
    return false;
  }

  const existingJob = jobs.get(storedRecord.key);
  if (
    existingJob?.record?.uploadId === storedRecord.uploadId &&
    storedRecord.state === "pending"
  ) {
    return true;
  }
  if (existingJob) abortJob(existingJob);
  const record = { ...storedRecord, persisted: true };
  records.set(record.key, record);
  if (!VISIBLE_STATES.has(record.state)) {
    releasePreviewUrl(record.key);
    removeEntry(record.key);
    return true;
  }
  const previewUrl = createPreviewUrl(record);
  publishEntry(record.key, entryFromRecord(record, previewUrl));
  if (record.state === "pending") enqueueJob(createJob(record));
  return true;
}

async function persistVolatileRecords(ownerEmail) {
  const volatile = Array.from(records.values()).filter(
    (record) =>
      record.ownerEmail === ownerEmail && record.persisted === false,
  );
  for (const record of volatile) {
    try {
      const persisted = await persistRecord(record);
      const current = records.get(record.key);
      if (
        persisted &&
        current?.uploadId === record.uploadId &&
        current.persisted === false
      ) {
        current.persisted = true;
        publishEntry(
          record.key,
          entryFromRecord(current, snapshot[record.key]?.previewUrl || null),
        );
      }
    } catch {
      return false;
    }
  }
  return true;
}

export function enqueueServiceHistoryUpload({
  vehicleId,
  ownerEmail,
  file,
  apiBaseUrl: requestApiBaseUrl = null,
}) {
  if (!file) {
    throw new TypeError("enqueueServiceHistoryUpload benötigt eine Datei.");
  }
  const normalizedOwner = normalizeEmail(ownerEmail);
  if (!currentOwnerEmail && normalizedOwner) {
    currentOwnerEmail = normalizedOwner;
    publishStatus({ ownerEmail: normalizedOwner });
  }
  const uploadId = createUploadId();
  const key = makeServiceHistoryUploadKey(vehicleId, uploadId);
  const record = {
    key,
    ownerEmail: normalizedOwner,
    vehicleId: String(vehicleId || ""),
    uploadId,
    generation: 1,
    capturedAt: Date.now(),
    blob: file,
    fileName: file.name || "service-history-file",
    contentType: file.type || "application/octet-stream",
    lastModified: Number(file.lastModified || Date.now()),
    apiBaseUrl: String(requestApiBaseUrl || apiBaseUrl || "").replace(
      /\/+$/,
      "",
    ),
    state: "pending",
    phase: "persisting",
    attempts: 0,
    nextRetryAt: null,
    serverRequestStarted: false,
    retryable: true,
    errorName: null,
    errorCode: null,
    errorMessage: null,
    httpStatus: null,
    imageUrl: null,
    prepared: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    persisted: true,
  };

  records.set(key, record);
  const job = createJob(record);
  const previewUrl = createPreviewUrl(record);
  publishEntry(key, entryFromRecord(record, previewUrl));

  const persisted = (async () => {
    try {
      if (!registryStatus.persistenceAvailable) {
        throw new Error("IndexedDB ist vorübergehend nicht verfügbar.");
      }
      const didPersist = await persistRecord(record);
      if (!isJobCurrent(job) || !didPersist) {
        settle(job, { ok: false, cancelled: true });
        return { ok: false, cancelled: true };
      }
      record.phase = "queued";
      record.updatedAt = Date.now();
      publishEntry(key, entryFromRecord(record, previewUrl));
      enqueueJob(job);
      return { ok: true };
    } catch (error) {
      console.error(
        "Serviceheft-Datei konnte nicht in IndexedDB gespeichert werden:",
        error,
      );
      record.persisted = false;
      record.phase = "queued";
      publishStatus({ persistenceAvailable: false });
      publishEntry(key, entryFromRecord(record, previewUrl));
      enqueueJob(job);
      return { ok: false, error };
    }
  })();

  return {
    uploadId,
    persisted,
    completion: job.completion.promise,
  };
}

export function resolveServiceHistoryUploadServerState(entry, carData) {
  if (!entry || !carData) return { kind: "none" };
  const version =
    carData?.photoUploadVersions?.serviceHistory?.[entry.uploadId] || null;
  const serviceHistory = Array.isArray(carData.serviceHistory)
    ? carData.serviceHistory
    : [];
  const mainUrl = String(version?.mainUrl || "").trim();
  const versionMatchesUpload =
    version?.uploadId === entry.uploadId &&
    Number(version?.generation) === Number(entry.generation || 1);
  if (
    versionMatchesUpload &&
    version?.status === "main_uploaded" &&
    mainUrl &&
    serviceHistory.includes(mainUrl)
  ) {
    return { kind: "exact", imageUrl: mainUrl };
  }
  if (versionMatchesUpload && version?.status === "deleted") {
    return { kind: "deleted", imageUrl: null };
  }
  return { kind: "none" };
}

export async function acknowledgeServiceHistoryUploadServerState({
  vehicleId,
  uploadId,
  serverState,
}) {
  if (!["exact", "deleted"].includes(serverState?.kind)) return false;
  const key = makeServiceHistoryUploadKey(vehicleId, uploadId);
  const record = records.get(key);
  if (!record || record.uploadId !== uploadId) return false;

  const job = jobs.get(key);
  if (job) {
    abortJob(job, {
      ok: serverState.kind === "exact",
      imageUrl: serverState.imageUrl || null,
      serverConfirmed: true,
      deleted: serverState.kind === "deleted",
    });
    if (jobs.get(key) === job) jobs.delete(key);
  }
  clearRetryTimer(key);
  records.delete(key);
  releasePreviewUrl(key);
  removeEntry(key);
  try {
    await deleteServiceHistoryUploadRecord(key, uploadId);
  } catch (error) {
    console.error(
      "Bestätigter Serviceheft-Upload konnte lokal nicht entfernt werden:",
      error,
    );
    publishStatus({ persistenceAvailable: false });
  }
  return true;
}

export async function discardFailedServiceHistoryUpload(uploadId) {
  const entry = Object.values(snapshot).find(
    (candidate) => candidate?.uploadId === uploadId,
  );
  if (!entry || entry.state !== "failed") return false;
  const record = records.get(entry.key);
  if (!record || record.uploadId !== uploadId) return false;
  if (discardRequests.has(entry.key)) return false;

  discardRequests.add(entry.key);
  record.phase = "cancelling";
  publishEntry(
    entry.key,
    entryFromRecord(record, snapshot[entry.key]?.previewUrl || null),
  );

  try {
    await cancelFailedRecord(record);

    const current = records.get(entry.key);
    if (!current || current.uploadId !== uploadId) return true;

    const job = jobs.get(entry.key);
    if (job) abortJob(job);
    jobs.delete(entry.key);
    records.delete(entry.key);
    releasePreviewUrl(entry.key);
    removeEntry(entry.key);
    try {
      await deleteServiceHistoryUploadRecord(entry.key, uploadId);
    } catch (error) {
      console.error(
        "Bestätigt verworfener Serviceheft-Upload blieb in IndexedDB:",
        error,
      );
      publishStatus({ persistenceAvailable: false });
    }
    return true;
  } catch (error) {
    console.error(
      "Serviceheft-Upload konnte serverseitig nicht verworfen werden:",
      error,
    );
    const current = records.get(entry.key);
    if (current?.uploadId === uploadId) {
      current.phase = null;
      publishEntry(
        entry.key,
        entryFromRecord(
          current,
          snapshot[entry.key]?.previewUrl || null,
        ),
      );
    }
    return false;
  } finally {
    discardRequests.delete(entry.key);
  }
}

export function hasUnsafeServiceHistoryUploads(uploadSnapshot = snapshot) {
  return Object.values(uploadSnapshot).some(
    (entry) =>
      entry?.hasLocalBlob === true &&
      ["pending", "failed"].includes(entry?.state),
  );
}

export async function awaitVehicleServiceHistoryUploads(vehicleId) {
  if (hydrationPromise) await hydrationPromise;
  if (!registryStatus.hydrated) {
    return {
      ok: false,
      failedEntries: [],
      pendingEntries: [
        {
          vehicleId,
          state: "pending",
          phase: "hydrating",
        },
      ],
    };
  }
  if (
    currentOwnerEmail &&
    !registryStatus.storageDefinitelyUnavailable
  ) {
    try {
      const stored = await loadRecordsByOwner(currentOwnerEmail);
      stored
        .filter((record) => String(record.vehicleId) === String(vehicleId))
        .forEach(adoptStoredRecord);
    } catch (error) {
      console.error(
        "Serviceheft-Uploads konnten vor Abschluss nicht geprüft werden:",
        error,
      );
      return {
        ok: false,
        failedEntries: [],
        pendingEntries: [
          {
            vehicleId,
            state: "pending",
            phase: "persistence_unavailable",
          },
        ],
      };
    }
  }
  const entries = Object.values(snapshot).filter(
    (entry) => String(entry?.vehicleId) === String(vehicleId),
  );
  const failedEntries = entries.filter((entry) => entry.state === "failed");
  // "committed" is emitted only after the upload endpoint has atomically
  // written Firestore and returned the canonical URL. The listener still
  // removes the local record later, but final navigation need not wait for it.
  const pendingEntries = entries.filter((entry) => entry.state === "pending");
  return {
    ok: failedEntries.length === 0 && pendingEntries.length === 0,
    failedEntries,
    pendingEntries,
  };
}

function resetRuntime(ownerEmail) {
  hydrationGeneration += 1;
  hydrationPromise = null;
  queue.length = 0;
  jobs.forEach((job) => abortJob(job));
  jobs.clear();
  retryTimers.forEach((timer) => clearTimeout(timer));
  retryTimers.clear();
  activeJob = null;
  previewUrls.forEach((url) => globalThis.URL?.revokeObjectURL?.(url));
  previewUrls.clear();
  records.clear();
  snapshot = EMPTY_SNAPSHOT;
  currentOwnerEmail = ownerEmail;
  registryStatus = Object.freeze({
    hydrated: false,
    ownerEmail,
    persistenceAvailable: true,
    storageDefinitelyUnavailable: false,
  });
  emit();
}

export async function initializeServiceHistoryUploadRegistry({
  apiBaseUrl: nextApiBaseUrl,
  ownerEmail,
} = {}) {
  apiBaseUrl = String(nextApiBaseUrl || apiBaseUrl || "").replace(/\/+$/, "");
  const normalizedOwner = normalizeEmail(ownerEmail);
  if (normalizedOwner !== currentOwnerEmail) resetRuntime(normalizedOwner);
  if (
    registryStatus.hydrated &&
    registryStatus.persistenceAvailable &&
    registryStatus.ownerEmail === normalizedOwner
  ) {
    return registryStatus;
  }
  if (hydrationPromise) return hydrationPromise;

  publishStatus({ hydrated: false, ownerEmail: normalizedOwner });
  const generation = hydrationGeneration;
  hydrationPromise = (async () => {
    try {
      const stored = normalizedOwner
        ? await loadRecordsByOwner(normalizedOwner)
        : [];
      if (generation !== hydrationGeneration) return registryStatus;
      stored.forEach(adoptStoredRecord);
      await persistVolatileRecords(normalizedOwner);
      publishStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: true,
        storageDefinitelyUnavailable: false,
      });
    } catch (error) {
      if (generation !== hydrationGeneration) return registryStatus;
      console.error(
        "Gespeicherte Serviceheft-Uploads konnten nicht geladen werden:",
        error,
      );
      const storageDefinitelyUnavailable =
        typeof globalThis.indexedDB === "undefined" ||
        ["SecurityError", "NotSupportedError"].includes(error?.name);
      publishStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: false,
        storageDefinitelyUnavailable,
      });
    } finally {
      if (generation === hydrationGeneration) hydrationPromise = null;
    }

    if (!onlineListenerInstalled && typeof window !== "undefined") {
      onlineListenerInstalled = true;
      window.addEventListener("online", () => {
        jobs.forEach((job) => {
          if (!isJobCurrent(job)) return;
          clearRetryTimer(job.key);
          enqueueJob(job);
        });
      });
    }
    return registryStatus;
  })();
  return hydrationPromise;
}

export async function __resetServiceHistoryUploadRegistryForTests() {
  resetRuntime(null);
  registryStatus = EMPTY_STATUS;
  apiBaseUrl = "";
  uploadTimeoutOverrideMs = null;
  loadRecordsByOwner = getServiceHistoryUploadRecordsByOwner;
  persistRecord = putServiceHistoryUploadRecord;
}

export function __setServiceHistoryUploadTimeoutForTests(timeoutMs) {
  uploadTimeoutOverrideMs = Math.max(1, Number(timeoutMs) || 1);
}

export function __setServiceHistoryUploadStoreAdaptersForTests({
  loadRecords,
  saveRecord,
} = {}) {
  loadRecordsByOwner =
    typeof loadRecords === "function"
      ? loadRecords
      : getServiceHistoryUploadRecordsByOwner;
  persistRecord =
    typeof saveRecord === "function"
      ? saveRecord
      : putServiceHistoryUploadRecord;
}
